import React from 'react';
import { Link } from 'react-router-dom';
import Tooltip from '@material-ui/core/Tooltip';
import { FormControl, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { css, StyleSheet } from 'aphrodite';

import { ApiHelper } from '../../common/helpers/ApiHelper';
import { Helper } from '../../common/helpers/Helper';
import { UiHelper } from '../../common/helpers/UiHelper';
import { BaseEntityPage } from '../../common/components/BaseEntityPage';
import { SearchBar } from '../../common/components/SearchBar';
import { AppConfig } from '../../AppConfig';
import { Controller } from '../../common/Controller';
import { AppStyles, AppTheme } from '../../styles/AppTheme';
import { CommonTheme } from '../../styles/Styles';

const ACCOUNT_FILTER_ALL = { key: 'all', label: 'All Zoho accounts' }

function getDeleteFormDef(current) {
  const domainName = (current && current.domainName) || ''
  return {
    title: domainName
      ? `${Helper.getString('zohoDomainsDeleteTitle')}: ${domainName}`
      : Helper.getString('zohoDomainsDeleteTitle'),
    description: Helper.getString('zohoDomainsDeleteWarn'),
    steps: [{ fields: [] }],
  }
}

function formatStatus(value) {
  if (value === true || value === 'true') return 'Yes'
  if (value === false || value === 'false') return 'No'
  if (value === null || value === undefined || value === '') return '-'
  return String(value)
}

export class ZohoDomains extends BaseEntityPage {
  constructor(props) {
    super(props);
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.orgId = 'PRODUCT'
    this.pageTitle = Helper.getString('zohoDomains')
    this.pageKey = 'zohoDomains'
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: Controller.get().getHomeByRole() },
      { title: this.pageTitle },
    ]
    this.toFetch = [
      { ms: this.ms, method: 'GET', paths: ['miscs', 'list-zoho-domains'] },
    ]
    this.apiData = {}
    this.noAdd = true
    this.emptyText = Helper.getString('zohoDomainsEmpty')
    this.accounts = {}
    this.tableHeadersMap = {
      Domain: (current) => current.domainName || '',
      Account: (current) => this.accountTitle(current.zoid),
      Hosting: (current) => formatStatus(current.mailHostingEnabled),
      Verification: (current) => formatStatus(current.verificationStatus),
      MX: (current) => formatStatus(current.mxstatus),
      SPF: (current) => formatStatus(current.spfstatus),
      DKIM: (current) => formatStatus(current.dkimstatus),
      Primary: (current) => formatStatus(current.primary),
      ZOID: (current) => current.zoid || '',
    }
    this.tableHeaders = Object.keys(this.tableHeadersMap)
    this.headerActions = [
      {
        label: 'Refresh',
        icon: 'refresh',
        onClick: () => this.fetchItems(),
      },
    ]
  }

  accountTitle(zoid) {
    const account = this.accounts && this.accounts[zoid]
    return (account && account.title) || zoid || ''
  }

  getAccountOptions() {
    const options = Object.keys(this.accounts || {}).map(zoid => ({
      key: zoid,
      label: this.accounts[zoid].title || zoid,
    })).sort((a, b) => a.label.localeCompare(b.label))
    return [ACCOUNT_FILTER_ALL, ...options]
  }

  onFetchSuccess(results) {
    const data = results[0] || {}
    this.accounts = data.accounts || {}
    const items = (data.items || []).map((item, index) => ({
      ...item,
      id: item.domainName || `${item.zoid || 'zoid'}-${index}`,
      __index: index,
    }))
    this.setState({
      masterItemsList: items,
      items,
      searchText: '',
      accountFilterSelected: ACCOUNT_FILTER_ALL,
    })
  }

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) { return '' }
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }

  openModalForDelete(current) {
    this.deletingItem = current
    this.fieldDef = this.formDefinition = getDeleteFormDef(current)
    this.apiData = {}
    super.openModalForDelete(current, this.formDefinition)
  }

  onDelete() {
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({
        ms: this.ms,
        paths: ['miscs', 'delete-zoho-domains'],
      }),
    });
  }

  customSubmit(options) {
    const { formType, caller } = options
    if (formType !== 'delete') {
      return caller.submit.call(caller, options.formData, true)
    }
    const item = this.deletingItem || {}
    ApiHelper.callAwait({
      method: 'POST',
      jsonBody: {
        domains: [item.domainName],
      },
      endPoint: ApiHelper.makeUrlPath2({
        ms: this.ms,
        paths: ['miscs', 'delete-zoho-domains'],
      }),
    }).then(() => {
      this.deletingItem = null
      this.setState({
        modalOpen: false,
        showToast: true,
        isErrorToast: false,
        toastMsg: Helper.getString('zohoDomainsDeleteSuccess'),
      })
      this.fetchItems()
    }).catch(err => {
      const errorMessage = Helper.getErrorMsg(err)
      caller.setState({
        fetchState: ApiHelper.State.ERROR,
        errMsg: errorMessage,
        success: false,
        message: errorMessage,
        inProcess: false,
      })
    })
  }

  onActions(current) {
    const actions = []
    if (current.zoid && current.domainName) {
      const to = `/admin/zoho-inboxes?zoid=${encodeURIComponent(current.zoid)}&domain=${encodeURIComponent(current.domainName)}`
      actions.push(
        <Link to={to} key={(current.id || current.domainName) + '-inboxes'}>
          <Tooltip title={Helper.getString('zohoViewInboxes')} placement='top-start'>
            <span className={css(AppStyles.entityTableLinkIcon)}>
              <i className='material-icons-outlined'>inbox</i>
            </span>
          </Tooltip>
        </Link>
      )
    }
    actions.push(UiHelper.buttonDelete(this, current))
    return actions
  }

  applyFilters(searchText = '', accountFilterSelected = ACCOUNT_FILTER_ALL) {
    const search = (searchText || '').toLowerCase().trim()
    const selected = accountFilterSelected || ACCOUNT_FILTER_ALL
    const zoidFilter = selected.key === 'all' ? '' : selected.key
    let items = [...(this.state.masterItemsList || [])]
    if (zoidFilter) {
      items = items.filter(item => String(item.zoid) === String(zoidFilter))
    }
    if (search) {
      items = items.filter(item => {
        const account = this.accountTitle(item.zoid).toLowerCase()
        return (
          (item.domainName && item.domainName.toLowerCase().includes(search)) ||
          (item.zoid && String(item.zoid).toLowerCase().includes(search)) ||
          (item.verificationStatus && String(item.verificationStatus).toLowerCase().includes(search)) ||
          account.includes(search)
        )
      })
    }
    this.setState({
      items,
      searchText,
      accountFilterSelected: selected,
    })
  }

  onSearch = (searchText = '') => {
    this.applyFilters(searchText, this.state.accountFilterSelected)
  }

  clearSearch = () => {
    this.applyFilters('', this.state.accountFilterSelected)
  }

  onAccountFilterChange = (event, val) => {
    this.applyFilters(this.state.searchText, val || ACCOUNT_FILTER_ALL)
  }

  renderAboveTable() {
    const accountOptions = this.getAccountOptions()
    const multiAccount = accountOptions.length > 2
    const accountValue = this.state.accountFilterSelected || ACCOUNT_FILTER_ALL

    return (
      <div className={css(Styles.toolbar)}>
        {multiAccount && (
          <div className={css(Styles.filterContainer)}>
            <div className={css(Styles.formControlContainer)}>
              <p className={css(Styles.label)}>Account</p>
              <FormControl className={css(Styles.formControlMainFilters)} variant='outlined'>
                <Autocomplete
                  value={accountValue}
                  options={accountOptions}
                  onChange={this.onAccountFilterChange}
                  getOptionLabel={option => option.label || option.key || ''}
                  getOptionSelected={(option, v) => option.key === v.key}
                  disableClearable
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant='standard'
                      className={css(Styles.formControlFilters)}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        readOnly: true,
                        classes: {
                          root: css([Styles.textInputStyles, Styles.autoCompleteInputRoot]),
                          input: css(Styles.formInputStyle, Styles.textInput),
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </div>
          </div>
        )}
        <div className={css(Styles.searchBarContainer)} onSubmit={e => e.preventDefault()}>
          <SearchBar
            hint={Helper.getString('zohoDomainsSearchHint')}
            onSearch={this.onSearch}
            onClearSearch={this.clearSearch}
            searchText={this.state.searchText}
            searchButtonLabel='Filter'
            clearSearchLabel='Clear'
            autoRefresh
            debounceDuration={1}
            autoFocus={false}
            hideFiltersContainer
          />
        </div>
      </div>
    )
  }
}

const Styles = StyleSheet.create({
  toolbar: {
    width: '100%',
    marginBottom: 16,
  },
  filterContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 16,
    overflowX: 'hidden',
    overflowY: 'visible',
    minWidth: 0,
  },
  formControlContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 5,
    flex: '1 1 220px',
    minWidth: 0,
    maxWidth: 320,
  },
  label: {
    fontSize: 14,
    color: CommonTheme.subTextColor,
    margin: 0,
    display: 'inline-block',
  },
  formControlMainFilters: {
    width: '100%',
  },
  formControlFilters: {
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden',
  },
  textInputStyles: {
    padding: '11px 20px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16,
  },
  autoCompleteInputRoot: {
    paddingRight: 48,
  },
  formInputStyle: {
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  textInput: {
    padding: '4px 0px',
  },
  searchBarContainer: {
    width: '100%',
    minWidth: 0,
    overflowX: 'hidden',
  },
})
